import type { ReactNode, UIEvent } from 'react';
import { useMemo } from 'react';
import styled from 'styled-components';

import PrimaryText from 'components/core/typography/PrimaryText';
import Text from 'components/core/typography/Text';
import Button from 'components/ui/shared/Buttons/Button';

import { CARD_WIDTH } from 'styles/layouts';
import { FONT_SIZE_24, NEUTRAL_0, SPACE_300, SPACE_700 } from 'styles/tokens';

const Container = styled.div`
  align-items: center;
  background: ${NEUTRAL_0};
  display: flex;
  flex-flow: column;
  margin: 0 auto;
  min-width: ${CARD_WIDTH};
  padding-top: 180px;
  width: 100%;
`;

const Icon = styled.div`
  margin-bottom: ${SPACE_300};
`;

const Title = styled(PrimaryText)`
  font-size: ${FONT_SIZE_24};
  margin-bottom: ${SPACE_300};
  text-align: center;
  white-space: normal;
`;

const ActionButton = styled(Button)`
  &:not(:last-child) {
    margin-bottom: 10px;
  }
`;

const PlaceholderText = styled(Text)`
  margin-bottom: ${SPACE_700};
  text-align: center;
  white-space: normal;
  width: calc(100% - ${SPACE_700});
`;

interface PlaceholderProps {
  buttonText?: string | string[];
  className?: string;
  icon?: ReactNode;
  /**
   * Callback for when a button is clicked. An index number is passed to determine which button was clicked
   */
  onClick?: (e: UIEvent<HTMLElement>, n: number) => void;
  subtitle: string;
  testId?: string;
  title: string;
}

const Placeholder = ({ buttonText, className, icon, onClick, subtitle, testId, title }: PlaceholderProps) => {
  const buttons = useMemo(() => {
    if (!onClick || !buttonText) {
      return null;
    }

    const texts = Array.isArray(buttonText) ? buttonText : [buttonText];

    return texts.map((text, index) => (
      <ActionButton key={`${text}-${index}`} onClick={e => onClick(e, index)} variant="primary">
        {text}
      </ActionButton>
    ));
  }, [buttonText, onClick]);

  return (
    <Container className={className} data-testid={testId}>
      {icon ? <Icon>{icon}</Icon> : null}
      <Title>{title}</Title>
      <PlaceholderText>{subtitle}</PlaceholderText>
      {buttons}
    </Container>
  );
};

export const BackgroundlessPlaceholder = styled(Placeholder)`
  background: none;
`;

export default Placeholder;
